import { useEffect, useState } from "react";

const STORAGE_KEY = "ps.colWidths";
// 7 fixed columns: START, FINISH, DUR, ROOM, TASK, STATUS, NOTES
const DEFAULT_FIXED = [64, 64, 64, 132, 320, 96, 200];
const DEFAULT_CREW = 86;
const MIN_W = 48;
const MAX_W = 640;

/**
 * Persisted column widths for the schedule grid.
 *
 * Stores:
 *   - `fixed`:     array of widths for the 7 leading columns
 *   - `crewById`:  per-crew-id width override, so each column can be resized
 *                  independently (drag its own right-edge handle)
 *   - `crewDefault`: width applied to crew columns without an override
 *                    (legacy single-width key — kept for backward compat).
 */
export function useColumnWidths() {
  const [fixedWidths, setFixedWidths] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (Array.isArray(s?.fixed) && s.fixed.length === DEFAULT_FIXED.length) return s.fixed;
    } catch (e) {
      console.warn("ps.colWidths parse failed", e);
    }
    return DEFAULT_FIXED;
  });

  const [crewColDefault, setCrewColDefault] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (typeof s?.crew === "number" && s.crew >= 56) return s.crew;
    } catch (e) {
      console.warn("ps.colWidths.crew parse failed", e);
    }
    return DEFAULT_CREW;
  });

  const [crewColById, setCrewColById] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (s?.crewById && typeof s.crewById === "object") return { ...s.crewById };
    } catch (e) {
      console.warn("ps.colWidths.crewById parse failed", e);
    }
    return {};
  });

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ fixed: fixedWidths, crew: crewColDefault, crewById: crewColById }),
    );
  }, [fixedWidths, crewColDefault, crewColById]);

  // {kind: "fixed" | "crew" | "crew-default", index?, crewId?, startX, origW}
  const [colResize, setColResize] = useState(null);

  const beginColResize = (kind, indexOrId, e) => {
    e.preventDefault();
    e.stopPropagation();
    let origW;
    if (kind === "fixed") origW = fixedWidths[indexOrId];
    else if (kind === "crew") origW = crewColById[indexOrId] ?? crewColDefault;
    else origW = crewColDefault; // legacy "all crew columns" handle
    setColResize({ kind, index: indexOrId, startX: e.clientX, origW });
  };

  useEffect(() => {
    if (!colResize) return;
    const onMove = (e) => {
      const delta = e.clientX - colResize.startX;
      const next = Math.max(MIN_W, Math.min(MAX_W, colResize.origW + delta));
      if (colResize.kind === "fixed") {
        setFixedWidths((arr) => {
          const copy = [...arr];
          copy[colResize.index] = next;
          return copy;
        });
      } else if (colResize.kind === "crew") {
        // Per-crew override.
        setCrewColById((prev) => ({ ...prev, [colResize.index]: next }));
      } else {
        setCrewColDefault(next);
      }
    };
    const onUp = () => setColResize(null);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [colResize]);

  const fixedTotal = fixedWidths.reduce((s, w) => s + w, 0);

  const leftOffsets = fixedWidths.reduce((acc, w, i) => {
    acc.push(i === 0 ? 0 : acc[i - 1] + fixedWidths[i - 1]);
    return acc;
  }, []);

  // Convenience: width-for-crew lookup function. Uses per-id override else default.
  const crewWidthFor = (crewId) => crewColById[crewId] ?? crewColDefault;

  return {
    fixedWidths,
    crewColDefault,
    crewColById,
    crewWidthFor,
    fixedTotal,
    leftOffsets,
    beginColResize,
  };
}
